import type { Axis, CellKind, Coord, Puzzle } from '@arrow/puzzle-schema';
import type { ModelAnswer, PuzzleModel } from './types.ts';

const LETTER = /^[A-Z]$/;

function kindOf(ch: string | undefined): CellKind {
	if (ch !== undefined && LETTER.test(ch)) return 'letter';
	if (ch === '#') return 'clue';
	return 'blank';
}

const AXIS_RANK: Record<Axis, number> = { across: 0, down: 1 };

/**
 * Derives the lookup tables the engine and UI need. Assumes the puzzle has already
 * passed `validatePuzzle`; it does not re-validate (an answer running off the grid is
 * clipped rather than thrown on).
 */
export function buildModel(puzzle: Puzzle): PuzzleModel {
	const { width, height } = puzzle;
	const size = width * height;
	const kinds: CellKind[] = [];
	const solution: string[] = [];
	const letterCells: number[] = [];
	for (let row = 0; row < height; row++) {
		const line = puzzle.grid[row] ?? '';
		for (let col = 0; col < width; col++) {
			const ch = line[col];
			const kind = kindOf(ch);
			kinds.push(kind);
			solution.push(kind === 'letter' ? (ch as string) : '');
			if (kind === 'letter') letterCells.push(row * width + col);
		}
	}

	const clueByAnswer = new Map<string, string>();
	const clues: PuzzleModel['clues'] = {};
	const cluesByCell: Record<number, string[]> = {};
	for (const clue of puzzle.clues) {
		clues[clue.id] = clue;
		if (!clueByAnswer.has(clue.answerId)) clueByAnswer.set(clue.answerId, clue.id);
		const idx = clue.cell.row * width + clue.cell.col;
		(cluesByCell[idx] ??= []).push(clue.id);
	}

	const answers: Record<string, ModelAnswer> = {};
	const answersByCell: Record<number, string[]> = {};
	for (const cell of letterCells) answersByCell[cell] = [];
	for (const answer of puzzle.answers) {
		const cells: number[] = [];
		for (let i = 0; i < answer.text.length; i++) {
			const row = answer.start.row + (answer.axis === 'down' ? i : 0);
			const col = answer.start.col + (answer.axis === 'across' ? i : 0);
			if (row < 0 || row >= height || col < 0 || col >= width) break;
			cells.push(row * width + col);
		}
		answers[answer.id] = {
			id: answer.id,
			text: answer.text,
			...(answer.display ? { display: answer.display } : {}),
			axis: answer.axis,
			cells,
			clueId: clueByAnswer.get(answer.id) ?? '',
		};
		for (const cell of cells) (answersByCell[cell] ??= []).push(answer.id);
	}
	// Keep each cell's answers in a stable order: across first.
	for (const key of Object.keys(answersByCell)) {
		const list = answersByCell[Number(key)]!;
		list.sort((a, b) => AXIS_RANK[answers[a]!.axis] - AXIS_RANK[answers[b]!.axis]);
	}

	// Clue-reading order: by the clue cell (top-to-bottom, left-to-right), across before
	// down within a cell. An answer without a clue sorts by its start cell, after ties.
	const sortKey = (a: (typeof puzzle.answers)[number]): [number, number, number] => {
		const clue = clues[clueByAnswer.get(a.id) ?? ''];
		const at = clue ? clue.cell.row * width + clue.cell.col : a.start.row * width + a.start.col;
		return [at, AXIS_RANK[a.axis], a.start.row * width + a.start.col];
	};
	const answerOrder = [...puzzle.answers]
		.sort((a, b) => {
			const ka = sortKey(a);
			const kb = sortKey(b);
			return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
		})
		.map((a) => a.id);

	const orderRank = new Map(answerOrder.map((id, i) => [id, i]));
	// A two-clue cell lists its clues in answer order (across first).
	const clueRank = (clueId: string) => orderRank.get(clues[clueId]!.answerId) ?? Number.MAX_SAFE_INTEGER;
	for (const key of Object.keys(cluesByCell)) cluesByCell[Number(key)]!.sort((a, b) => clueRank(a) - clueRank(b));

	const crossings: Record<string, string[]> = {};
	for (const id of answerOrder) {
		const set = new Set<string>();
		for (const cell of answers[id]!.cells) {
			for (const other of answersByCell[cell] ?? []) if (other !== id) set.add(other);
		}
		crossings[id] = [...set].sort((a, b) => orderRank.get(a)! - orderRank.get(b)!);
	}

	return {
		puzzle,
		width,
		height,
		size,
		kinds,
		letterCells,
		solution,
		answers,
		answerOrder,
		answersByCell,
		crossings,
		clues,
		cluesByCell,
	};
}

export function cellIndex(model: PuzzleModel, coord: Coord): number {
	return coord.row * model.width + coord.col;
}

export function cellCoord(model: PuzzleModel, index: number): Coord {
	return { row: Math.floor(index / model.width), col: index % model.width };
}

export function isLetterCell(model: PuzzleModel, index: number): boolean {
	return model.kinds[index] === 'letter';
}

/**
 * A clue's enumeration: the length of each word, the way a newspaper prints "(5)" or, for
 * a multi-word answer, "(2,3)" (space → comma) and "(4-4)" (hyphen kept). It reads the
 * word breaks from the answer's `display` ("BLACK CAT"); a single-word answer is just its
 * letter count. `spoken` is the same for a screen reader ("2 and 3 letters").
 */
export function enumeration(answer: Pick<ModelAnswer, 'display' | 'cells'>): { label: string; spoken: string } {
	const letters = answer.cells.length;
	const stripped = (answer.display ?? '').replace(/[^A-Za-z]/g, '');
	// Use the display only when it matches the grid letters and actually has a break.
	const usable = answer.display && stripped.length === letters && /[ -]/.test(answer.display);
	if (!usable) return { label: String(letters), spoken: `${letters} letter${letters === 1 ? '' : 's'}` };
	const label = answer.display!.trim()
		.split(/\s+/)
		.map((word) => word.split('-').map((part) => part.replace(/[^A-Za-z]/g, '').length).join('-'))
		.join(',');
	const nums = label.split(/[,-]/).map(Number);
	const spoken = `${nums.length === 1 ? nums[0] : `${nums.slice(0, -1).join(', ')} and ${nums.at(-1)}`} letters`;
	return { label, spoken };
}
